import React, { useEffect, useMemo, useState } from "react";
import { conversationAPI } from "../services/api";
import { Conversation, Message } from "../types";
import { MessageSquare, ShieldOff, ShieldCheck, Users } from "lucide-react";

const ChatConsole: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingConvos, setLoadingConvos] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<"all" | "takeover" | "ai">("all");
  const [search, setSearch] = useState("");
  const [composer, setComposer] = useState("");

  useEffect(() => {
    fetchConversations();
    // Preload with dummy data if empty on first load
    preloadDummyIfEmpty();
  }, []);

  useEffect(() => {
    if (selectedId) {
      fetchMessages(selectedId);
    } else {
      setMessages([]);
    }
  }, [selectedId]);

  const fetchConversations = async () => {
    setLoadingConvos(true);
    setError("");
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const guestId = urlParams.get("guestId") || undefined;
      const data = await conversationAPI.list(guestId);
      if (guestId && data.length > 0) {
        setSelectedId(data[0].id);
      }
      setConversations(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load conversations");
    } finally {
      setLoadingConvos(false);
    }
  };

  const preloadDummyIfEmpty = async () => {
    // Wait briefly for real fetch; if still empty, set dummy data
    setTimeout(() => {
      setConversations((existing) => {
        if (existing.length > 0) return existing;
        const dummyConvos: Conversation[] = [
          {
            id: "dummy-1",
            guest_name: "John Smith",
            room_number: "101",
            agent_takeover: false,
            last_message_at: new Date().toISOString(),
            channel: "whatsapp",
            status: "open",
          },
          {
            id: "dummy-2",
            guest_name: "Sarah Johnson",
            room_number: "202",
            agent_takeover: true,
            last_message_at: new Date().toISOString(),
            channel: "whatsapp",
            status: "open",
          },
        ];
        setMessages([
          {
            id: "msg-1",
            conversation_id: "dummy-1",
            sender_type: "customer",
            content: "Hi, I need extra towels in room 101.",
            created_at: new Date().toISOString(),
          },
          {
            id: "msg-2",
            conversation_id: "dummy-1",
            sender_type: "ai",
            content: "Sure, I will notify housekeeping and have towels sent shortly.",
            created_at: new Date().toISOString(),
          },
          {
            id: "msg-3",
            conversation_id: "dummy-2",
            sender_type: "customer",
            content: "My AC isn't working well.",
            created_at: new Date().toISOString(),
          },
          {
            id: "msg-4",
            conversation_id: "dummy-2",
            sender_type: "agent",
            content: "I’m on it—will dispatch maintenance and follow up.",
            created_at: new Date().toISOString(),
          },
        ]);
        return dummyConvos;
      });
    }, 500);
  };

  const fetchMessages = async (id: string) => {
    setLoadingMessages(true);
    setError("");
    try {
      const data = await conversationAPI.listMessages(id);
      setMessages(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSend = async () => {
    if (!composer.trim() || !selectedId) return;
    setSendLoading(true);
    setError("");
    try {
      await conversationAPI.sendAgentMessage(selectedId, composer.trim());
      setComposer("");
      await fetchMessages(selectedId);
      await fetchConversations();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to send message");
    } finally {
      setSendLoading(false);
    }
  };

  const handleToggleTakeover = async (id: string, current: boolean) => {
    setError("");
    try {
      await conversationAPI.setTakeover(id, !current);
      await fetchConversations();
      if (selectedId === id) await fetchMessages(id);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update takeover");
    }
  };

  const filteredConvos = useMemo(() => {
    return conversations.filter((c) => {
      const matchesFilter =
        filter === "all"
          ? true
          : filter === "takeover"
          ? !!c.agent_takeover
          : !c.agent_takeover;
      const matchesSearch =
        (c.guest_name || "")
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        (c.room_number || "").toLowerCase().includes(search.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [conversations, filter, search]);

  const selectedConversation = conversations.find((c) => c.id === selectedId) || null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-black-guestby flex items-center space-x-2">
          <MessageSquare className="h-7 w-7 text-blue-guestby" />
          <span>Chat Console</span>
        </h1>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md border border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-grey-guestby">Filter</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
              >
                <option value="all">All</option>
                <option value="takeover">Agent takeover</option>
                <option value="ai">AI responding</option>
              </select>
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search guest or room"
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby w-full"
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingConvos ? (
              <div className="p-4 text-sm text-grey-guestby">Loading conversations...</div>
            ) : filteredConvos.length === 0 ? (
              <div className="p-4 text-sm text-grey-guestby">No conversations.</div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {filteredConvos.map((c) => (
                  <li
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${
                      selectedId === c.id ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-black-guestby">
                          {c.guest_name || "Guest"} — Room {c.room_number || "—"}
                        </p>
                        <p className="text-xs text-grey-guestby">
                          Last: {c.last_message_at ? new Date(c.last_message_at).toLocaleString() : "—"}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {c.agent_takeover ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                            Agent
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800">
                            AI
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-gray-200 lg:col-span-2 flex flex-col">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-black-guestby">
                {selectedConversation
                  ? `${selectedConversation.guest_name || "Guest"} — Room ${selectedConversation.room_number || "—"}`
                  : "Select a conversation"}
              </p>
              <p className="text-xs text-grey-guestby">Agent takeover prevents AI replies for this conversation.</p>
            </div>
            {selectedConversation && (
              <button
                onClick={() =>
                  handleToggleTakeover(selectedConversation.id, !!selectedConversation.agent_takeover)
                }
                className={`flex items-center space-x-2 px-3 py-2 text-xs rounded-md ${
                  selectedConversation.agent_takeover
                    ? "bg-green-100 text-green-800"
                    : "bg-amber-100 text-amber-800"
                }`}
              >
                {selectedConversation.agent_takeover ? (
                  <>
                    <ShieldCheck className="h-4 w-4" />
                    <span>Agent takeover</span>
                  </>
                ) : (
                  <>
                    <ShieldOff className="h-4 w-4" />
                    <span>AI active</span>
                  </>
                )}
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {loadingMessages ? (
              <div className="text-sm text-grey-guestby">Loading messages...</div>
            ) : messages.length === 0 ? (
              <div className="text-sm text-grey-guestby">No messages yet.</div>
            ) : (
              messages.map((m) => (
                <div
                  key={m.id}
                  className={`max-w-xl p-3 rounded-lg border ${
                    m.sender_type === "agent"
                      ? "bg-blue-50 border-blue-100 text-black-guestby"
                      : m.sender_type === "customer"
                      ? "bg-white border-gray-200 text-black-guestby"
                      : "bg-slate-50 border-slate-200 text-black-guestby"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold uppercase text-grey-guestby">
                      {m.sender_type}
                    </span>
                    <span className="text-xxs text-grey-guestby">
                      {new Date(m.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t border-gray-200 space-y-3">
            <textarea
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              placeholder="Type an agent reply..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
              rows={3}
              disabled={!selectedConversation}
            />
            <div className="flex items-center justify-between">
              <div className="text-xs text-grey-guestby">
                {selectedConversation
                  ? selectedConversation.agent_takeover
                    ? "Agent replies only. AI is paused."
                    : "AI still active; toggle Agent takeover to pause AI."
                  : "Select a conversation to reply."}
              </div>
              <button
                onClick={handleSend}
                disabled={!selectedConversation || sendLoading || !composer.trim()}
                className="px-4 py-2 bg-blue-guestby text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {sendLoading ? "Sending..." : "Send as Agent"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatConsole;

